import { useRequiredContext } from "@redotech/react-util/context";
import ClockIcon from "@redotech/redo-icons/clock.svg";
import CoinsIcon from "@redotech/redo-icons/coins-01.svg";
import ShieldTickIcon from "@redotech/redo-icons/shield-tick.svg";
import TrophyIcon from "@redotech/redo-icons/trophy-01.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { JSXElementConstructor, memo, useCallback, useState } from "react";
import { OnboardingProps } from "../../../feature-onboarding/common";

export const DisputesWelcomeModal = memo(function DisputesWelcomeModal({
  handleDismiss,
  handleComplete,
}: OnboardingProps) {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [pending, setPending] = useState(false);

  const handleGetStarted = useCallback(async () => {
    setPending(true);
    try {
      await rpcClient.updatePickedSettings({
        teamId: team._id,
        settings: { "disputes/automateEvidenceEnabled": true },
      });
      handleComplete();
    } catch {
      // Leave modal open on error
    } finally {
      setPending(false);
    }
  }, [rpcClient, team._id, handleComplete]);

  return (
    <RedoModal
      hideCloseButton
      hideFooter
      isOpen
      modalSize="sm"
      onModalCloseRequested={(reason) => {
        if (reason !== "backdropClick") {
          handleDismiss();
        }
      }}
      title={
        <Text fontSize="xl" fontWeight="semibold" textColor="primary">
          Never handle chargebacks again, let Redo fight for you
        </Text>
      }
    >
      <Flex dir="column" gap="xl" pb="xl">
        <Text fontSize="sm" textColor="secondary">
          We handle the entire dispute process: evidence gathering, response
          filing, and follow-up. You only pay when we win.
        </Text>
        <Flex dir="column" gap="md">
          <ProofPoint
            Icon={TrophyIcon}
            text="Estimated 60% win rate, 3x the industry average."
          />
          <ProofPoint Icon={ShieldTickIcon} text="4x ROI guarantee." />
          <ProofPoint
            Icon={CoinsIcon}
            text="Free unless we win. 25% success fee on recovered revenue."
          />
          <ProofPoint
            Icon={ClockIcon}
            text="Get 2+ hours back per chargeback. We handle everything automatically."
          />
        </Flex>
        <Flex dir="column" gap="sm">
          <RedoButton
            hierarchy="primary"
            onClick={handleGetStarted}
            pending={pending}
            size="lg"
            text="Start winning chargebacks with Reclaim AI"
          />
          <Flex justify="center">
            <RedoButton
              hierarchy="link"
              onClick={handleDismiss}
              text="No thanks, I'll fight them manually"
            />
          </Flex>
        </Flex>
      </Flex>
    </RedoModal>
  );
});

const ProofPoint = memo(function ProofPoint({
  Icon,
  text,
}: {
  Icon: JSXElementConstructor<{ className?: string }>;
  text: string;
}) {
  return (
    <Flex align="center" gap="md">
      <Flex shrink={0}>
        <RedoFeaturedIcon color="brand" Icon={Icon} size="sm" />
      </Flex>
      <Text fontSize="sm" textColor="secondary">
        {text}
      </Text>
    </Flex>
  );
});
